import { KeyValuePipe, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { PrescriptionTypes } from '@lib/prescriptions/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { BehaviorSubject, catchError, debounceTime, distinctUntilChanged, filter, map, of, switchMap, tap } from 'rxjs';

import { MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';

import { DEFAULT_PAGE_SIZE, EMPTY_RESPONSE } from '@lib/commons/constants/request.const';
import { clearEmptyField } from '@lib/commons/util-functions/clear-empty-field.function';

import { PagedResponse } from '@lib/core/models/paged-response';
import { AppointmentSearchModel, AppointmentTypes, Visit, VisitStatusTypes, VisitTypes } from '@lib/core/models/visit.interface';

import { PeriodFilterComponent, PeriodFilterValue } from '@lib/shared-ui/commons/period-filter/period-filter.component';
import { SpinLoadingComponent } from '@lib/shared-ui/commons/spin-loading/spin-loading.component';
import { InfiniteScrollDirective } from '@lib/shared-ui/directives/infinite-scroll.directive';
import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

import { VisitsService } from '../../../../core/services/visits.service';
import { AppointmentsTableComponent } from '../../tables/appointments-table/appointments-table.component';

enum AppointmentTypeFilters {
  All = 'All',
  RelatedSpecialists = 'RelatedSpecialists',
  InstrumentalDiagnostic = 'InstrumentalDiagnostic',
  LaboratoryService = 'LaboratoryService',
  Drugs = 'Drugs',
  Appointment = 'Appointment',
}

enum AppointmentStatusFilters {
  All = 'All',
  ToDo = 'ToDo',
  Done = 'Done',
  Rejected = 'Rejected',
}

interface AppointmentsFiltersControls {
  type: FormControl<AppointmentTypeFilters>;
  search: FormControl<string>;
  dateFrom: FormControl<Date | null>;
  dateTo: FormControl<Date | null>;
  status: FormControl<AppointmentStatusFilters>;
  period: FormControl<PeriodFilterValue | null>;
}

interface AppointmentsFilters {
  type: AppointmentTypeFilters;
  search: string;
  dateFrom: Date | null;
  dateTo: Date | null;
  status: AppointmentStatusFilters;
}

@Component({
  selector: 'hm-appointments-list',
  standalone: true,
  imports: [
    AppointmentsTableComponent,
    MatDivider,
    MatMenuModule,
    NgOptimizedImage,
    MatIcon,
    MatIconButton,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInput,
    InfiniteScrollDirective,
    LoaderComponent,
    SpinLoadingComponent,
    TranslatePipe,
    KeyValuePipe,
    PeriodFilterComponent,
  ],
  styles: `
    :host {
      @apply h-full;
    }
    ::ng-deep .mat-mdc-menu-panel {
      min-width: 400px !important;
    }
  `,
  templateUrl: './appointments-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppointmentsListComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly visitsService = inject(VisitsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackBar = inject(MatSnackBar);

  protected readonly statusFilter = AppointmentStatusFilters;
  protected readonly typeFilter = AppointmentTypeFilters;
  protected readonly visitTypes = AppointmentTypes;

  withSearch = input<boolean>(true);
  withPatientName = input<boolean>(true);
  clientUserId = input<string>();

  protected appointmentsFilterForm!: FormGroup<AppointmentsFiltersControls>;
  protected nextFetchedPage: number = 1;

  protected readonly loading = signal<boolean>(false);
  protected readonly appointmentsFetching = signal<boolean>(false);
  protected readonly appointments = signal<Visit[]>([]);
  protected readonly hasNextPage = signal<boolean>(true);

  protected sorting: {
    sortBy: string | null;
    direction: 'asc' | 'desc' | null;
  } = { sortBy: 'appointment_date', direction: 'desc' };

  protected readonly filters$ = new BehaviorSubject<AppointmentsFilters>({
    type: AppointmentTypeFilters.All,
    search: '',
    dateFrom: null,
    dateTo: null,
    status: AppointmentStatusFilters.All,
  });

  protected get filtersFc() {
    return this.appointmentsFilterForm.controls;
  }

  ngOnInit(): void {
    this.initFiltersForm();
    this.initAppointmentsFetcher();
    this.applyFilters();
  }

  private initFiltersForm() {
    this.appointmentsFilterForm = this.fb.group<AppointmentsFiltersControls>({
      type: this.fb.control(AppointmentTypeFilters.All),
      search: this.fb.control(''),
      dateFrom: this.fb.control<Date | null>(null),
      dateTo: this.fb.control<Date | null>(null),
      status: this.fb.control(AppointmentStatusFilters.All),
      period: this.fb.control<PeriodFilterValue | null>(null),
    });
    this.watchPeriodFilter();
    this.watchAllFilters();
  }

  private watchAllFilters() {
    this.appointmentsFilterForm.valueChanges
      .pipe(
        debounceTime(300),
        map(form => ({
          search: form.search?.trim().length >= 3 ? form.search.trim() : '',
          status: form.status || AppointmentStatusFilters.All,
          dateFrom: form.dateFrom,
          dateTo: form.dateTo,
          type: form.type || AppointmentTypeFilters.All,
        })),
        distinctUntilChanged(
          (prev, curr) =>
            prev.search === curr.search &&
            prev.status === curr.status &&
            prev.type === curr.type &&
            (prev.dateFrom?.getTime() ?? null) === (curr.dateFrom?.getTime() ?? null) &&
            (prev.dateTo?.getTime() ?? null) === (curr.dateTo?.getTime() ?? null)
        ),
        tap(() => this.resetPagination()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(filters => this.filters$.next(filters));
  }

  private watchPeriodFilter() {
    this.filtersFc.period.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(periodValue => {
      if (periodValue) {
        this.filtersFc.dateFrom.setValue(periodValue.dateFrom, { emitEvent: false });
        this.filtersFc.dateTo.setValue(periodValue.dateTo, { emitEvent: false });
      } else {
        this.filtersFc.dateFrom.setValue(null, { emitEvent: false });
        this.filtersFc.dateTo.setValue(null, { emitEvent: false });
      }
      this.applyFilters();
    });
  }

  protected fetchNextPage() {
    if (this.hasNextPage()) this.filters$.next(this.getFilters());
  }

  private initAppointmentsFetcher() {
    this.filters$
      .pipe(
        filter(() => this.hasNextPage()),
        tap(() => this.appointmentsFetching.set(true)),
        switchMap(filters =>
          this.visitsService.searchMemberAppointments(this.createAppointmentsPayload(filters)).pipe(
            catchError(() => {
              this.snackBar.open('Не вдалось підвантажити візити', 'OK', { duration: 10000 });
              return of(<PagedResponse<Visit>>EMPTY_RESPONSE);
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: result => {
          const data: Visit[] = result.value || [];
          this.hasNextPage.set(result.hasNextPage);

          if (this.nextFetchedPage === 1) this.appointments.set(data);
          else this.appointments.update(appointments => [...appointments, ...data]);

          this.nextFetchedPage++;
          this.appointmentsFetching.set(false);
          this.loading.set(false);
        },
        error: () => {
          this.appointmentsFetching.set(false);
          this.loading.set(false);
        },
      });
  }

  protected clearSearch() {
    this.filtersFc.search.setValue('');
  }

  protected clearFilter(filterType: 'type' | 'status' | 'period', event: Event) {
    event.stopPropagation();
    const actions = {
      type: () => this.filtersFc.type.setValue(AppointmentTypeFilters.All),
      status: () => this.filtersFc.status.setValue(AppointmentStatusFilters.All),
      period: () => this.clearDates(),
    };
    actions[filterType]();
  }

  private clearDates() {
    this.filtersFc.dateFrom.setValue(null);
    this.filtersFc.dateTo.setValue(null);
    this.applyFilters();
  }

  private applyFilters() {
    this.resetPagination();
    this.filters$.next(this.getFilters());
  }

  protected setTypeFilter(value: keyof typeof AppointmentTypeFilters) {
    this.filtersFc.type.setValue(AppointmentTypeFilters[value]);
  }

  protected setStatusFilter(value: AppointmentStatusFilters) {
    this.filtersFc.status.setValue(value);
  }

  private resetPagination() {
    this.loading.set(true);
    this.hasNextPage.set(true);
    this.appointments.set([]);
    this.nextFetchedPage = 1;
  }

  protected updateAppointmentsList() {
    this.applyFilters();
  }

  private getFilters(): AppointmentsFilters {
    const form = this.appointmentsFilterForm.value;
    return {
      search: form.search?.trim().length >= 3 ? form.search.trim() : '',
      status: form.status || AppointmentStatusFilters.All,
      dateFrom: form.dateFrom,
      dateTo: form.dateTo,
      type: form.type || AppointmentTypeFilters.All,
    };
  }

  protected createAppointmentsPayload(filters: AppointmentsFilters) {
    const { type, search, status, dateFrom, dateTo } = filters;

    const payload: AppointmentSearchModel = {
      userId: this.clientUserId() ?? null,
      page: this.nextFetchedPage,
      pageSize: DEFAULT_PAGE_SIZE,
      type: VisitTypes.AppointmentToDo,
      status: status !== AppointmentStatusFilters.All ? VisitStatusTypes[status] : null,
      sortBy: this.sorting.sortBy,
      sortDirection: this.sorting.direction,
      from: dateFrom,
      to: dateTo,
      metadata: {
        name: this.withSearch() && search ? search : null,
        type: type !== AppointmentTypeFilters.All ? PrescriptionTypes[type] : null,
      },
    };
    if (payload.metadata.name === null) delete payload.metadata.name;
    if (payload.metadata.type === null) delete payload.metadata.type;
    if (payload.metadata.name === null && payload.metadata.type === null) delete payload.metadata;

    return clearEmptyField(payload);
  }

  protected onSortChanged(sortData: { sortBy: string | null; sortDirection: 'asc' | 'desc' | null }): void {
    this.sorting = { sortBy: sortData.sortBy, direction: sortData.sortDirection };
    this.appointmentsFetching.set(true);
    this.hasNextPage.set(true);
    this.nextFetchedPage = 1;
    this.filters$.next(this.getFilters());
  }
}
